import z from "zod";

export enum SORT_TYPES {
    ALPHABETICAL = "ALPHABETICAL",
    FACH_OVERVIEW = "FACH_OVERVIEW",
    GRATISTEST = "GRATISTEST",
}
export enum BEITRAG_TYPES {
    IN1 = "IN1",
    MM1 = "MM1",
    ST1 = "ST1",
    ST2 = "ST2",
}

export enum PRODUCT_VARIANTS {
    BRO_BCH = "BRO_BCH",
    BRO_EBK = "BRO_EBK",
    BRO_THH = "BRO_THH",
    BTR_CDR = "BTR_CDR",
    BTR_DIG = "BTR_DIG",
    BTR_MUM = "BTR_MUM",
    BTR_MUV = "BTR_MUV",
    BTR_PRN = "BTR_PRN",
    SET_ABP = "SET_ABP",
    SET_ABX = "SET_ABX",
    SET_BCH = "SET_BCH",
    SET_ELP = "SET_ELP",
    SET_MIX = "SET_MIX",
    SET_PRN = "SET_PRN",
    SON_GWH = "SON_GWH",
    SON_GWK = "SON_GWK",
    SON_ORD = "SON_ORD",
    SON_WEB = "SON_WEB",
}

const RSearchFiltersScheme = z.object({
    schularten: z.record(z.string(), z.string()),
    klassenstufen: z.record(z.string(), z.string()),
    faecher: z.record(z.string(), z.string()),
    lernjahre: z.record(z.string(), z.string()),
});

const RFachScheme = z.object({
    sys: z.object({
        id: z.string(),
    }),
    fields: z.object({
        id: z.string(),
        displayValue: z.string(),
    }),
});

export const RFaecherScheme = z.array(RFachScheme);

export type RSearchFiltersType = z.infer<typeof RSearchFiltersScheme>;

export const RFiltersScheme = z.object({
    schularten: z.array(z.string()),
    klassenstufen: z.array(z.string()),
    faecher: z.array(z.string()),
    productVariant: z.array(z.string()),
});

export type RFiltersType = z.infer<typeof RFiltersScheme>;

export enum FACH_KUERZEL {
    GGR = "GGR",
    GLE = "GLE",
}

export type RProductContent =
    | string
    | ProductContentType
    | ProductTableType
    | ProductImageType
    | ProductListType;

export type ProductContentType = {
    clazz: string;
    content: RProductContent[];
};

const ContentElements = z.lazy(() =>
    z.union([z.string(), ProductContent, ProductTable, ProductImage, ProductList]),
);

const ProductContent: z.ZodType<ProductContentType> = z.lazy(() =>
    z.object({
        content: z
            .array(z.any())
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            .transform((arr) => arr.filter((item) => ContentElements.safeParse(item).success)),
        clazz: z.string(),
    }),
);

const ProductList = z.object({
    clazz: z.literal("- topic/ul "),
    li: z.array(ProductContent),
});

const ProductImage = z.object({
    href: z.string(),
    alt: z.string().optional(),
    clazz: z.literal("- topic/image "),
});

const ProductTable = z.object({
    tgroup: z.array(
        z.object({
            tbody: z.object({
                row: z.array(z.object({ entry: z.array(ProductContent) })),
            }),
        }),
    ),
    clazz: z.string(),
});

export type ProductTableType = z.infer<typeof ProductTable>;
export type ProductListType = z.infer<typeof ProductList>;
export type ProductImageType = z.infer<typeof ProductImage>;

export type RProductType = z.infer<typeof RProductScheme>;

export const RProductScheme = z.object({
    key: z.string(),
    rnummer: z.string(),
    version: z.number(),
    metadata: z.object({
        beitragsType: z.nativeEnum(BEITRAG_TYPES).optional(),
        isInRaabits: z.boolean(),
        portalonlineab: z.string(),
        erstertitelteil: z.string(),
        zweitertitelteil: z.string().optional(),
        fach: z.string(),
        beschreibungstext: z.string(),
        schularten: z.object({
            schulart: z.array(z.string()),
        }),
        klassenstufen: z.object({
            klassenstufe: z.array(z.string()),
        }),
        kompetenzen: z.object({
            kompetenz: z.array(z.string()),
        }),
        schlagworte: z.object({
            schlagwort: z.array(z.string()),
        }),
        lernjahre: z
            .object({
                lernjahr: z.array(z.string()),
            })
            .optional(),
        unterrichtsdauer: z
            .object({
                unterrichtsdauerVon: z.number().optional(),
                unterrichtsdauerBis: z.number().optional(),
            })
            .optional(),
        dateien: z.object({
            coverbild: z.string().optional(),
            leseprobe: z.string().optional(),
            deckblatt: z.string().optional(),
        }),
    }),
    productVariant: z
        .object({
            extendedAttributes: z.object({
                productVariant: z.nativeEnum(PRODUCT_VARIANTS).optional(),
                schoolType: z.string().optional().default("MISSING"),
                headtitle: z.string().optional().default("MISSING"),
                title1DeDe: z.string().optional().default("MISSING"),
                title2DeDe: z.string().optional().default("MISSING"),
                title3DeDe: z.string().optional().default("MISSING"),
                title4DeDe: z.string().optional().default("MISSING"),
                subjectArea: z.string().optional().default("MISSING"),
                year: z.string().optional().default("MISSING"),
                releaseDate: z.string().optional().default("MISSING"),
                bulletpoints: z.string().optional().default("MISSING"),
                components: z.string().optional().default("MISSING"),
                descHtmlDeDe: z.string().optional().default("MISSING"),
                descHtmlDeCh: z.string().optional().default("MISSING"),
                descPlainDeCh: z.string().optional().default("MISSING"),
                product: z.string().optional().default("MISSING"),
                targetGroup: z.string().optional().default("MISSING"),
                raaKlassenstufen: z.string().optional().default("MISSING"),
            }),
            price: z.object({
                price: z.number().transform((val) => Math.round(val * 100)),
                groupPrices: z
                    .array(
                        z.object({
                            group: z.string(),
                            price: z.number(),
                            objNr: z.string(),
                        }),
                    )
                    .optional()
                    .default([]),
            }),
            websites: z
                .array(
                    z.object({
                        storeviews: z.array(
                            z.object({
                                languageAttributes: z.object({
                                    description: z.string(),
                                }),
                            }),
                        ),
                    }),
                )
                .optional(),
        })
        .optional(),
    kompetenzProfil: ProductContent.optional(),
    allesAufEinenBlick: z
        .object({
            commonTitle: ProductContent,
            learningContentbody: z.object({
                section: z.array(ProductContent),
            }),
        })
        .optional(),
});

export const RSearchProductScheme = z.object({
    isInRaabits: z.boolean(),
    rnummer: z.string(),
    version: z.number(),
    title: z.string(),
    subtitle: z.string().optional(),
    description: z.string(),
    filterMetadata: z.object({
        schularten: z.array(z.string()),
        klassenstufen: z.array(z.string()),
        fach: z.string(),
        lernjahre: z.array(z.string()),
    }),
    beitragsType: z.nativeEnum(BEITRAG_TYPES).optional(),
    coverimage: z.string().optional(),
    portalonlineab: z.string(),
    isPruefansicht: z.boolean(),
    key: z.string(),
    productVariant: z
        .object({
            extendedAttributes: z.object({
                productVariant: z.nativeEnum(PRODUCT_VARIANTS).optional(),
                schoolType: z.string().optional().default("MISSING"),
                subjectArea: z.string().optional().default("MISSING"),
                raaKlassenstufen: z.string().optional().default("MISSING"),
            }),
            price: z.object({
                price: z.number().transform((val) => Math.round(val * 100)),
                groupPrices: z
                    .array(
                        z.object({
                            group: z.string(),
                            price: z.number(),
                            objNr: z.string(),
                        }),
                    )
                    .optional()
                    .default([]),
            }),
        })
        .optional(),
});

export type RSearchProductType = z.infer<typeof RSearchProductScheme>;

export const RSearchProductsScheme = z.object({
    produkte: z.array(RSearchProductScheme),
    totalResults: z.number(),
});
export type RSearchProductsType = z.infer<typeof RSearchProductsScheme>;

export const isRProductString = (item: RProductContent | undefined): item is string =>
    typeof item === "string";

export const isRProductTable = (item: RProductContent | undefined): item is ProductTableType =>
    !isRProductString(item) && typeof item === "object" && "tgroup" in item;

export const isRProductImage = (item: RProductContent | undefined): item is ProductImageType =>
    !isRProductString(item) &&
    typeof item === "object" &&
    "clazz" in item &&
    item.clazz === "- topic/image ";

export const isRProductList = (item: RProductContent | undefined): item is ProductListType =>
    !isRProductString(item) &&
    typeof item === "object" &&
    "clazz" in item &&
    item.clazz === "- topic/ul ";

export const isRProductContent = (item: RProductContent | undefined): item is ProductContentType =>
    !isRProductString(item) && typeof item === "object" && "content" in item;
